import { SiteNav, SiteFooter } from '@/components/ui/SiteNav'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Link } from 'react-router-dom'

// §0.1 audit: "No external LLM by default" was inaccurate — Workers AI is Cloudflare's on-platform
// inference service. Corrected to accurately describe Workers AI data handling.

export function SecurityPage() {
  return (
    <div className="min-h-screen bg-paper">
      <SiteNav />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-16 space-y-12">
        <div className="space-y-4">
          <h1 className="text-4xl font-medium text-deep-ink" style={{ letterSpacing: '-0.02em' }}>Security &amp; Privacy</h1>
          <p className="text-lg text-slate-600">Echosome is designed for teams that handle sensitive research data. Here is how we protect it.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[
            {
              title: 'Multi-tenant isolation',
              desc: 'Every customer is a separate tenant. Data never crosses tenant boundaries. Enforced at the query, storage, and vector layers.',
            },
            {
              title: 'Cloudflare R2 object storage',
              desc: 'Uploaded files are stored in Cloudflare R2 with tenant-scoped key prefixes. Regional residency available on Enterprise (US / EU / APAC).',
            },
            {
              title: 'AI inference via Cloudflare Workers AI',
              desc: 'AI analysis runs on Workers AI, Cloudflare\'s on-platform inference service. Your data stays within Cloudflare\'s infrastructure and does not leave for a third-party LLM provider. Demo mode never sends uploaded data to any LLM. Tenants can disable Workers AI entirely on Enterprise.',
            },
            {
              title: 'PII detection on upload',
              desc: 'Every upload is scanned for email, phone, name, address, IP, customer IDs, and sensitive free text before any processing. Flagged fields are surfaced for your review before analysis begins.',
            },
            {
              title: 'Audit log',
              desc: 'Every state-changing action is written to a per-tenant audit log. Tenant owners can filter and export their own activity log at any time.',
            },
            {
              title: 'Magic-link auth',
              desc: 'No passwords. Email magic links with short-lived tokens stored in Cloudflare KV. Sessions expire automatically and can be revoked at any time from the tenant dashboard.',
            },
          ].map(item => (
            <Card key={item.title}>
              <h3 className="font-medium text-deep-ink mb-2">{item.title}</h3>
              <p className="text-sm text-slate-600">{item.desc}</p>
            </Card>
          ))}
        </div>

        <div className="bg-soft-panel rounded-xl border border-border-gray p-6 space-y-4">
          <h2 className="text-lg font-medium text-deep-ink">Cloudflare as infrastructure vendor</h2>
          <p className="text-sm text-slate-600 leading-relaxed">
            Echosome is built entirely on Cloudflare infrastructure: Workers (compute), D1 (database), R2 (object storage), KV (session store), and Workers AI (inference). Cloudflare is a sub-processor for all Echosome tenants. A full sub-processor list and Cloudflare DPA are available to Enterprise customers on request.
          </p>
          <p className="text-sm text-slate-600 leading-relaxed">
            Cloudflare Workers AI inference runs on Cloudflare's global network. Cloudflare's published terms govern how inference requests are handled; Echosome does not send uploaded files to Workers AI — only structured, de-identified inputs derived from your data after PII detection.
          </p>
        </div>

        <Card className="bg-paper-warm border-border-warm space-y-4">
          <h2 className="text-lg font-medium text-deep-ink">Enterprise &amp; Regulated Industries</h2>
          <p className="text-sm text-slate-600">
            For insurance, financial services, and healthcare teams: DPA, sub-processor list, security overview, and regional residency commitments are available on Enterprise. Echosome is decision support for research — final data governance stays with your team.
          </p>
          <p className="text-sm text-slate-600">
            Security overview available on request — contact <a href="mailto:security@echosome.com" className="text-echo-indigo underline">security@echosome.com</a>
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/app/demo">
              <Button variant="primary" size="sm">Start 30-day Agency Studio trial</Button>
            </Link>
          </div>
          <p className="text-xs text-slate-400">Enterprise procurement conversations happen in parallel — you don't need to wait to start using the product.</p>
        </Card>
      </main>
      <SiteFooter />
    </div>
  )
}
